'use client'
import { useSyncExternalStore, useMemo } from 'react'

export type TypeDevice = 'mobile' | 'tablet' | 'desktop'
type Orientation = 'landscape' | 'portrait'

interface MediaInfo {
  size: { width: number; height: number }
  deviceType: TypeDevice
  isMobile: boolean
  iOS: boolean
  isSafari: boolean
  mobileDevice: boolean
  mobileLandscape: boolean
  isTouchDevice: boolean
  deviceOrientation: Orientation
}

const BREAKPOINTS = {
  mobile: 375,
  tablet: 768,
  desktopXs: 992,
  desktop: 1440
} as const

// Cached default device info for SSR (single object reference)
const DEFAULT_DEVICE_INFO: MediaInfo = {
  size: { width: 1440, height: 900 },
  deviceType: 'desktop',
  isMobile: false,
  iOS: false,
  isSafari: false,
  mobileDevice: false,
  mobileLandscape: false,
  isTouchDevice: false,
  deviceOrientation: 'landscape'
}

// Cache for user agent info (only computed once)
const getUserAgentInfo = (() => {
  let cache: { isMobileDevice: boolean; isIOS: boolean; isSafari: boolean } | null = null

  return () => {
    if (!cache && typeof window !== 'undefined') {
      const UA = navigator.userAgent
      cache = {
        isMobileDevice: Boolean(UA.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i)),
        isIOS: Boolean(UA.match(/iPhone|iPad|iPod/i)),
        isSafari: UA.includes('Safari') && !UA.includes('Chrome')
      }
    }
    return cache || { isMobileDevice: false, isIOS: false, isSafari: false }
  }
})()

const getDeviceType = (width: number): TypeDevice =>
  width < BREAKPOINTS.tablet ? 'mobile' :
    width < BREAKPOINTS.desktopXs ? 'tablet' :
      'desktop'

const getOrientation = (width: number, height: number): Orientation =>
  width / height >= 1 ? 'landscape' : 'portrait'

// Create cached getSnapshot function
const createGetSnapshot = () => {
  let cachedSnapshot: MediaInfo = DEFAULT_DEVICE_INFO

  return (): MediaInfo => {
    if (typeof window === 'undefined') {
      return DEFAULT_DEVICE_INFO
    }

    const width = window.innerWidth
    const height = window.innerHeight
    const deviceType = getDeviceType(width)
    const { isMobileDevice, isIOS, isSafari } = getUserAgentInfo()
    const mobileLandscape = screen?.orientation?.angle === 90
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const deviceOrientation = getOrientation(width, height)

    // Only create new object if values actually changed
    if (
      cachedSnapshot.size.width !== width ||
      cachedSnapshot.size.height !== height ||
      cachedSnapshot.deviceType !== deviceType ||
      cachedSnapshot.mobileLandscape !== mobileLandscape ||
      cachedSnapshot.deviceOrientation !== deviceOrientation
    ) {
      cachedSnapshot = {
        size: { width, height },
        deviceType,
        isMobile: deviceType === 'mobile',
        iOS: isIOS,
        isSafari,
        mobileDevice: isMobileDevice,
        mobileLandscape,
        isTouchDevice,
        deviceOrientation
      }
    }

    return cachedSnapshot
  }
}

const getSnapshot = createGetSnapshot()

// Server snapshot (always returns the same cached object)
const getServerSnapshot = (): MediaInfo => DEFAULT_DEVICE_INFO

// Debounce utility
function debounce<T extends (...args: unknown[]) => void>(func: T, wait: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return function debounced(this: unknown, ...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), wait)
  }
}

export const useDeviceType = (wait = 200): MediaInfo => {
  // Create subscribe function with debouncing
  const subscribe = useMemo(() => {
    return (callback: () => void) => {
      const debouncedCallback = debounce(callback, wait)

      window.addEventListener('resize', debouncedCallback, { passive: true })
      window.addEventListener('orientationchange', callback, { passive: true })

      return () => {
        window.removeEventListener('resize', debouncedCallback)
        window.removeEventListener('orientationchange', callback)
      }
    }
  }, [wait])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

// Keep iOS export for backwards compatibility
export const iOS = () => {
  if (typeof navigator === 'undefined') return false
  const UA = navigator.userAgent
  return Boolean(UA.match(/iPhone|iPad|iPod/i))
}
